import React, { useCallback, useMemo, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  Wand2,
  Pencil,
  Trash2,
  RefreshCw,
  X,
} from "lucide-react";
import { ContentLog } from "../../../../types/dashboard";
import { BrandDetails } from "../../../../types/add-brand-types";
import { WeeklyPlanItem } from "../../../../types/weekly-plan";
import {
  useContentCalendar,
  CalendarDay,
} from "../../../../hooks/useContentCalendar";
import { getContentLogCalendarDate } from "../../../../utils/content-log-date";
import "./contentCalendar.css";

interface ContentCalendarProps {
  onEditPost: (log: ContentLog) => void;
  onDeletePost: (log: ContentLog) => Promise<boolean>;
  onScheduleNewPost: (date: Date, mode: "ai" | "own") => void;
  onAnalyticsItem?: (log: ContentLog) => void;
  onOpenPlanItem?: (item: WeeklyPlanItem) => void;
  brandDetails: BrandDetails;
}

const WEEKDAYS = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
const MAX_DAY_CELL_PLATFORM_ICONS = 2;

const normalizePlatformId = (platform: string): string => {
  const normalized = platform.toLowerCase();
  if (normalized === "linkedin") return "linkedin";
  if (normalized === "x") return "x";
  return normalized;
};

const dedupeMedia = (media: { type: string; url: string }[] = []) => {
  const seen = new Set<string>();
  return media.filter((item) => {
    const key = `${item.type}:${item.url}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getStatusBadgeClass = (status: string) => {
  switch (status.toLowerCase()) {
    case "published":
      return "cc-badge-published";
    case "scheduled":
      return "cc-badge-scheduled";
    case "draft":
      return "cc-badge-draft";
    default:
      return "cc-badge-default";
  }
};

const ContentCalendar: React.FC<ContentCalendarProps> = ({
  onEditPost,
  onDeletePost,
  onScheduleNewPost,
  onAnalyticsItem,
  onOpenPlanItem,
  brandDetails,
}) => {
  const {
    monthLabel,
    calendarDays,
    selectedDate,
    setSelectedDate,
    selectedDayLogs,
    selectedDayPlannedItems,
    selectedDateLabel,
    goToPrevMonth,
    goToNextMonth,
    getLogsForDay,
    getPlannedItemsForDay,
    getPlatformIcon,
    getPlatformName,
    formatTime,
    loading,
    fetchLogs,
    fetchPlannedItems,
    currentMonth,
    currentYear,
  } = useContentCalendar(brandDetails.id);

  const [showNewPostOptions, setShowNewPostOptions] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ContentLog | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handlePrev = useCallback(() => {
    goToPrevMonth();
  }, [goToPrevMonth]);

  const handleNext = useCallback(() => {
    goToNextMonth();
  }, [goToNextMonth]);

  const handleClosePanel = useCallback(() => {
    setSelectedDate(null);
  }, [setSelectedDate]);

  const handleDayClick = useCallback(
    (day: CalendarDay) => {
      if (!day.isCurrentMonth) return;
      setSelectedDate(day.date);
      setShowNewPostOptions(false);
    },
    [setSelectedDate],
  );

  const handleScheduleNew = useCallback(
    (mode: "ai" | "own") => {
      if (!selectedDate) return;
      onScheduleNewPost(selectedDate, mode);
      setShowNewPostOptions(false);
    },
    [onScheduleNewPost, selectedDate],
  );

  const resolvePlatform = useCallback((p: unknown): string => {
    if (typeof p === "string") return p;
    if (p && typeof p === "object" && "platform" in p)
      return (p as { platform: string }).platform;
    return "";
  }, []);

  const getLogPlatforms = useCallback(
    (log: ContentLog): string[] => {
      const rawPlatforms = log.content?.platforms ?? [];
      const resolvedPlatforms = rawPlatforms
        .map(resolvePlatform)
        .filter(Boolean)
        .map(normalizePlatformId);

      if (resolvedPlatforms.length > 0) {
        return Array.from(new Set(resolvedPlatforms));
      }

      return log.platform ? [normalizePlatformId(log.platform)] : [];
    },
    [resolvePlatform],
  );

  const combineLogsByPostId = useCallback(
    (logs: ContentLog[]): ContentLog[] => {
      const groupedLogs = new Map<string, ContentLog[]>();

      logs.forEach((log) => {
        const key = log.post_id || log.id;
        const items = groupedLogs.get(key) ?? [];
        items.push(log);
        groupedLogs.set(key, items);
      });

      return Array.from(groupedLogs.values()).map((items) => {
        if (items.length === 1) {
          const [singleItem] = items;
          return {
            ...singleItem,
            content: {
              ...singleItem.content,
              platforms: getLogPlatforms(singleItem),
            },
          };
        }

        const baseItem = items[0];
        const platforms = Array.from(
          new Set(items.flatMap((item) => getLogPlatforms(item))),
        );

        const platformCaptions = items.reduce<Record<string, string>>(
          (acc, item) => {
            const itemPlatforms = getLogPlatforms(item);
            const existingCaptions = item.content?.platformCaptions ?? {};

            itemPlatforms.forEach((platform) => {
              const caption =
                existingCaptions[platform] ??
                existingCaptions[normalizePlatformId(platform)] ??
                item.content?.content ??
                "";

              if (caption) {
                acc[platform] = caption;
              }
            });

            return acc;
          },
          {},
        );

        const platformMedia = items.reduce<
          Record<string, { type: string; url: string }[]>
        >((acc, item) => {
          const itemPlatforms = getLogPlatforms(item);
          const existingPlatformMedia = item.content?.platformMedia ?? {};

          itemPlatforms.forEach((platform) => {
            const mediaForPlatform = dedupeMedia(
              existingPlatformMedia[platform] ?? item.content?.media ?? [],
            );
            if (mediaForPlatform.length > 0) {
              acc[platform] = mediaForPlatform;
            }
          });

          return acc;
        }, {});

        const mediaSignatures = Object.values(platformMedia).map((media) =>
          media.map((item) => `${item.type}:${item.url}`).join("|"),
        );
        const uniqueMediaSignatures = new Set(
          mediaSignatures.filter((signature) => signature.length > 0),
        );
        const useCommonMediaForAllPlatforms = uniqueMediaSignatures.size <= 1;
        const commonMedia = useCommonMediaForAllPlatforms
          ? dedupeMedia(
              Object.values(platformMedia)[0] ?? baseItem.content?.media ?? [],
            )
          : dedupeMedia(baseItem.content?.media ?? []);

        return {
          ...baseItem,
          content: {
            ...baseItem.content,
            content:
              platformCaptions[platforms[0]] ?? baseItem.content?.content ?? "",
            platforms,
            platformCaptions,
            media: commonMedia,
            platformMedia:
              useCommonMediaForAllPlatforms ||
              Object.keys(platformMedia).length === 0
                ? undefined
                : platformMedia,
            useCommonMediaForAllPlatforms,
          },
        };
      });
    },
    [getLogPlatforms],
  );

  const groupedSelectedDayLogs = useMemo(
    () => combineLogsByPostId(selectedDayLogs),
    [selectedDayLogs, combineLogsByPostId],
  );

  const getLogDateTime = useCallback(
    (log: ContentLog): Date | null => getContentLogCalendarDate(log),
    []
  );

  const groupedDayLogsByKey = useMemo(() => {
    const entries: Array<[string, ContentLog[]]> = calendarDays.map((day) => [
      day.date.toDateString(),
      day.isCurrentMonth ? combineLogsByPostId(getLogsForDay(day.date)) : [],
    ]);
    return new Map<string, ContentLog[]>(entries);
  }, [calendarDays, combineLogsByPostId, getLogsForDay]);

  const plannedItemsByDayKey = useMemo(() => {
    const entries: Array<[string, WeeklyPlanItem[]]> = calendarDays.map((day) => [
      day.date.toDateString(),
      day.isCurrentMonth ? getPlannedItemsForDay(day.date) : [],
    ]);
    return new Map<string, WeeklyPlanItem[]>(entries);
  }, [calendarDays, getPlannedItemsForDay]);

  const isPastDay = useMemo(() => {
    if (!selectedDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sel = new Date(selectedDate);
    sel.setHours(0, 0, 0, 0);
    return sel < today;
  }, [selectedDate]);

  const canScheduleOnSelectedDate = useMemo(() => {
    if (!selectedDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const sel = new Date(selectedDate);
    sel.setHours(0, 0, 0, 0);
    return sel >= today;
  }, [selectedDate]);

  const handleRefresh = useCallback(() => {
    fetchLogs(currentYear, currentMonth, false);
    fetchPlannedItems(calendarDays);
  }, [calendarDays, currentMonth, currentYear, fetchLogs, fetchPlannedItems]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteTarget(null);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    const success = await onDeletePost(deleteTarget);
    setDeleting(false);
    if (success) {
      setDeleteTarget(null);
      fetchLogs(currentYear, currentMonth, true);
      fetchPlannedItems(calendarDays);
    }
  }, [calendarDays, currentMonth, currentYear, deleteTarget, fetchLogs, fetchPlannedItems, onDeletePost]);

  const renderDayCell = useCallback(
    (day: CalendarDay, index: number) => {
      const dayLogs = groupedDayLogsByKey.get(day.date.toDateString()) ?? [];
      const dayPlannedItems =
        plannedItemsByDayKey.get(day.date.toDateString()) ?? [];
      const isSelected =
        selectedDate &&
        day.isCurrentMonth &&
        day.date.toDateString() === selectedDate.toDateString();

      return (
        <div
          key={index}
          className={`cc-day-cell ${
            !day.isCurrentMonth ? "cc-day-outside" : ""
          } ${day.isToday ? "cc-day-today" : ""} ${
            isSelected ? "cc-day-selected" : ""
          }`}
          onClick={() => handleDayClick(day)}
        >
          <span className="cc-day-number">{day.day}</span>
          <div className="cc-day-pills">
            {dayLogs.slice(0, 3).map((log) => {
              const allPlatforms = getLogPlatforms(log);
              const displayPlatform =
                allPlatforms[0] || normalizePlatformId(log.platform);
              const pillPlatforms =
                allPlatforms.length > 0 ? allPlatforms : [displayPlatform];
              const visiblePlatforms = pillPlatforms.slice(
                0,
                MAX_DAY_CELL_PLATFORM_ICONS,
              );
              const hiddenPlatformCount = Math.max(
                pillPlatforms.length - visiblePlatforms.length,
                0,
              );
              const logDateTime = getLogDateTime(log);
              const time = logDateTime
                ? logDateTime.toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })
                : "";
              return (
                <div key={log.post_id || log.id} className="cc-day-pill">
                  <div className="cc-pill-icons">
                    {visiblePlatforms.map((platform) => (
                      <img
                        key={platform}
                        src={getPlatformIcon(platform) ?? ""}
                        alt={platform}
                        className="cc-pill-icon"
                      />
                    ))}
                    {hiddenPlatformCount > 0 && (
                      <span
                        className="cc-pill-icon-overflow"
                        aria-label={`${hiddenPlatformCount} more platforms`}
                      >
                        +{hiddenPlatformCount}
                      </span>
                    )}
                  </div>
                  {time && <span className="cc-pill-time">{time}</span>}
                </div>
              );
            })}
            {dayPlannedItems.slice(0, Math.max(0, 3 - dayLogs.length)).map((item) => (
              <div key={item.id} className="cc-day-pill cc-day-pill--planned">
                <span className="cc-pill-plan-dot" />
                <span className="cc-pill-time">
                  {new Date(item.planned_for).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                    hour12: false,
                  })}
                </span>
              </div>
            ))}
            {dayLogs.length > 3 && (
              <span className="cc-day-more">+{dayLogs.length - 3}</span>
            )}
            {dayLogs.length <= 3 && dayLogs.length + dayPlannedItems.length > 3 && (
              <span className="cc-day-more">
                +{dayLogs.length + dayPlannedItems.length - 3}
              </span>
            )}
          </div>
        </div>
      );
    },
    [
      getLogDateTime,
      getLogPlatforms,
      getPlatformIcon,
      groupedDayLogsByKey,
      handleDayClick,
      plannedItemsByDayKey,
      selectedDate,
    ],
  );

  const renderPlannedCard = useCallback(
    (item: WeeklyPlanItem) => {
      const plannedFor = new Date(item.planned_for);
      return (
        <div key={item.id} className="cc-post-card cc-post-card--planned">
          <div className="cc-post-card-header">
            <div>
              <div className="cc-post-card-name">
                {item.content_type === "seo_article"
                  ? "SEO article"
                  : item.platform || "Social post"}
              </div>
              <div className="cc-post-card-time">
                {formatTime(plannedFor.toISOString())}
              </div>
            </div>
            <span className="cc-status-badge cc-badge-planned">PLANNED</span>
          </div>
          <p className="cc-post-card-content">
            <strong>{item.topic}</strong>
            <br />
            {item.brief.length > 120 ? item.brief.slice(0, 120) + "..." : item.brief}
          </p>
          <div className="cc-post-card-actions">
            <button
              className="cc-edit-btn"
              onClick={() => onOpenPlanItem?.(item)}
            >
              Open
            </button>
          </div>
        </div>
      );
    },
    [formatTime, onOpenPlanItem],
  );

  const renderPostCard = useCallback(
    (log: ContentLog) => {
      const platforms = getLogPlatforms(log);
      const logDateTime = getLogDateTime(log);
      const time = logDateTime ? formatTime(logDateTime.toISOString()) : "";
      const status = log.status;
      const content = log.content?.content ?? "";
      const isDraftOrScheduled =
        status.toLowerCase() === "draft" ||
        status.toLowerCase() === "scheduled";
      const canEdit = isDraftOrScheduled && !isPastDay;

      return (
        <div key={log.post_id || log.id} className="cc-post-card">
          <div className="cc-post-card-header">
            <div className="cc-post-card-platform">
              <div className="cc-post-card-icons" aria-label="Platforms">
                {platforms.map((platform) => {
                  const icon = getPlatformIcon(platform);
                  const name = getPlatformName(platform);
                  if (!icon) return null;
                  return (
                    <img
                      key={platform}
                      src={icon}
                      alt={name}
                      className="cc-post-card-icon"
                    />
                  );
                })}
              </div>
              <div>
                <div className="cc-post-card-name">
                  {platforms
                    .map((platform) => getPlatformName(platform))
                    .join(", ")}
                </div>
                {time && <div className="cc-post-card-time">{time}</div>}
              </div>
            </div>
            <span className={`cc-status-badge ${getStatusBadgeClass(status)}`}>
              {status.toUpperCase()}
            </span>
          </div>
          <p className="cc-post-card-content">
            {content.length > 120 ? content.slice(0, 120) + "..." : content}
          </p>
          <div className="cc-post-card-actions">
            {canEdit ? (
              <button className="cc-edit-btn" onClick={() => onEditPost(log)}>
                Edit
              </button>
            ) : status.toLowerCase() === "published" &&
              log.content?.provider === "postforme" &&
              log.post_id &&
              onAnalyticsItem ? (
              <button
                className="cc-view-btn"
                style={{ color: "#7c3aed" }}
                onClick={() => onAnalyticsItem(log)}
              >
                Analytics
              </button>
            ) : (
              <button className="cc-view-btn" onClick={() => onEditPost(log)}>
                View
              </button>
            )}
            {!isPastDay && (
              <button
                className="cc-delete-btn"
                onClick={() => setDeleteTarget(log)}
                title="Delete post"
              >
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </div>
      );
    },
    [
      formatTime,
      getLogDateTime,
      getLogPlatforms,
      getPlatformIcon,
      getPlatformName,
      isPastDay,
      onAnalyticsItem,
      onEditPost,
    ],
  );

  return (
    <div className="cc-tab-container">
      <div className={`cc-layout ${selectedDate ? "cc-with-panel" : ""}`}>
        <div className="cc-calendar-section">
          <div className="cc-header">
            <h2 className="cc-title">Content Calendar</h2>
            <div className="cc-month-nav">
              <button className="cc-nav-btn" onClick={handlePrev}>
                <ChevronLeft size={18} />
              </button>
              <span className="cc-month-label">{monthLabel}</span>
              <button className="cc-nav-btn" onClick={handleNext}>
                <ChevronRight size={18} />
              </button>
            </div>
            <button
              className="cc-nav-btn"
              onClick={handleRefresh}
              title="Refresh"
              disabled={loading}
            >
              <RefreshCw size={16} className={loading ? "cc-spin" : ""} />
            </button>
          </div>

          <div className="cc-weekday-row">
            {WEEKDAYS.map((d) => (
              <div key={d} className="cc-weekday">
                {d}
              </div>
            ))}
          </div>

          <div className="cc-grid" style={{ position: "relative" }}>
            {calendarDays.map((day, i) => renderDayCell(day, i))}
            {loading && <div className="cc-grid-loading" />}
          </div>
        </div>

        {/* Right Panel */}
        {selectedDate && (
          <div className="cc-panel">
            <div className="cc-panel-header">
              <div>
                <div className="cc-panel-label">SELECTED DATE</div>
                <h3 className="cc-panel-date">{selectedDateLabel}</h3>
              </div>
              <button className="cc-panel-close" onClick={handleClosePanel}>
                <X size={18} />
              </button>
            </div>

            <div className="cc-panel-posts">
              {groupedSelectedDayLogs.length === 0 &&
              selectedDayPlannedItems.length === 0 ? (
                <div className="cc-panel-empty">
                  No posts scheduled or published for this day.
                </div>
              ) : (
                <>
                  {selectedDayPlannedItems.map((item) =>
                    renderPlannedCard(item)
                  )}
                  {groupedSelectedDayLogs.map((log) => renderPostCard(log))}
                </>
              )}
            </div>

            {canScheduleOnSelectedDate && (
              <div className="cc-panel-footer">
                {!showNewPostOptions ? (
                  <button
                    className="cc-schedule-btn"
                    onClick={() => setShowNewPostOptions(true)}
                  >
                    <Plus size={18} />
                    SCHEDULE NEW POST
                  </button>
                ) : (
                  <div className="cc-new-post-options">
                    <button
                      className="cc-option-btn cc-option-ai"
                      onClick={() => handleScheduleNew("ai")}
                    >
                      <Wand2 size={16} />
                      AI Generated
                    </button>
                    <button
                      className="cc-option-btn cc-option-own"
                      onClick={() => handleScheduleNew("own")}
                    >
                      <Pencil size={16} />
                      Create Own Post
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {deleteTarget && (
        <div className="cc-confirm-overlay" onClick={handleDeleteCancel}>
          <div
            className="cc-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="cc-confirm-icon">
              <Trash2 size={24} />
            </div>
            <h3 className="cc-confirm-title">Delete this post?</h3>
            <p className="cc-confirm-desc">
              This action cannot be undone. The post will be permanently
              removed.
            </p>
            <div className="cc-confirm-actions">
              <button
                className="cc-confirm-cancel"
                onClick={handleDeleteCancel}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="cc-confirm-delete"
                disabled={deleting}
                onClick={handleDeleteConfirm}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentCalendar;
